/**
 * Modulvorschau in der Shell (ADR-010, Nachtrag „P11 Vorschau“).
 *
 * Testern (`core.module.preview`) liefert das Bootstrap zu einem Modul zusätzlich den Eintrag
 * `vorschau`: eine hochgeladene, noch nicht freigegebene Version, die die API unter
 * `/api/vorschau/<modul>/*` neben der aktiven Version ausführt. Die Shell bietet dann „Vorschau
 * x.y.z testen“ an; im Vorschau-Zustand lädt sie das Bundle der Vorschau (eigene Prüfsumme,
 * Zwischenspeicher je `id@version`), lenkt `ctx.api.fetch` auf die Vorschau-Basis und zeigt
 * einen deutlichen Hinweis „Vorschau – nicht freigegeben, arbeitet auf echten Daten“ (P11b).
 *
 * Komfort, kein Schutz: Ob jemand die Vorschau sehen darf, entscheidet die API bei jeder Anfrage.
 * Reine Funktionen ohne DOM, damit sie testbar sind.
 */

export type VorschauModus = 'lesend' | 'testmandant' | 'schreibend';

/** Der Vorschau-Teil eines Manifest-Eintrags (nur für Tester). */
export interface VorschauEintrag {
  version: string;
  url: string;
  integrity: string;
  modus: VorschauModus;
  /** ISO-Zeitpunkt, bis zu dem die Vorschau läuft. */
  /** ISO-Zeitpunkt, seit dem die Vorschau läuft (sie läuft bis zur Freigabe oder zum Beenden). */
  gestartetAm: string;
  /** `vorschau/<modul>` — Pfad für `ctx.api.fetch`. */
  apiBasis: string;
  /** Es gibt keine aktive Version: das Modul ist nur als Vorschau da. */
  nurVorschau?: boolean;
  /** Im Mandanten (noch) nicht freigeschaltet — nur Testende sehen es (neues Modul). */
  ohneFreischaltung?: boolean;
}

/** Was die Vorschau-Funktionen von einem Manifest-Eintrag brauchen. */
export interface MitVorschau {
  id: string;
  url: string;
  integrity: string;
  version?: string;
  vorschau?: VorschauEintrag;
}

/**
 * Die Vorschau eines Eintrags, wenn sie gültig ist. Die API-Basis muss genau
 * `vorschau/<id>` sein — ein Eintrag kann `ctx.api.fetch` nicht auf ein anderes Modul lenken.
 */
export function vorschauVon(eintrag: MitVorschau): VorschauEintrag | null {
  const v = eintrag.vorschau;
  if (!v || typeof v !== 'object') return null;
  if (v.apiBasis !== `vorschau/${eintrag.id}`) return null;
  if (typeof v.url !== 'string' || !v.integrity?.startsWith('sha384-')) return null;
  if (!['lesend', 'testmandant', 'schreibend'].includes(v.modus)) return null;
  return v;
}

/**
 * Ob die Vorschau beim Öffnen gleich an ist: nie von selbst. Nicht freigegebener Code läuft im
 * Browser erst nach einem Klick (Sicherheitsreview L2) — auch bei einem Modul nur als Vorschau.
 */
export function vorschauAnfangs(eintrag: MitVorschau, gemerkt = false): boolean {
  return vorschauVon(eintrag) !== null && gemerkt;
}

/** Ob die Shell gerade gar nichts laden darf (Modul nur als Vorschau, Vorschau aus). */
export function nichtsLaden(eintrag: MitVorschau, vorschauAn: boolean): boolean {
  return vorschauVon(eintrag)?.nurVorschau === true && !vorschauAn;
}

/** Der Eintrag, den die Shell lädt: im Vorschau-Zustand Bundle, Prüfsumme und Version der Vorschau. */
export function geladenerEintrag<E extends MitVorschau>(eintrag: E, vorschauAn: boolean): E {
  const v = vorschauAn ? vorschauVon(eintrag) : null;
  if (!v) return eintrag;
  return { ...eintrag, url: v.url, integrity: v.integrity, version: v.version };
}

/** Basis für `ctx.api.fetch`: `<modul>` oder in der Vorschau `vorschau/<modul>`. */
export function apiBasis(eintrag: MitVorschau, vorschauAn: boolean): string {
  const v = vorschauAn ? vorschauVon(eintrag) : null;
  return v ? v.apiBasis : eintrag.id;
}

/** Wie der Hinweis den Modus beschreibt. */
export const MODUS_TEXT: Record<VorschauModus, string> = {
  lesend: 'eingeschränkt auf nur lesend – Speichern, Anträge und Benachrichtigungen gehen nicht',
  testmandant: 'eingeschränkt auf den Testmandanten – dort auf echten Daten',
  schreibend: 'alle Funktionen wie die freigegebene Version',
};

/** Zeitpunkt „17.10.2026, 18:30“ (Ortszeit der Person). */
function zeitpunkt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Text des Hinweises im Vorschau-Zustand. */
export function bannerText(v: VorschauEintrag): string {
  const seit = zeitpunkt(v.gestartetAm);
  return (
    `Version ${v.version} ist noch nicht freigegeben und arbeitet auf echten Daten: ` +
    `${MODUS_TEXT[v.modus]}. ` +
    (v.modus === 'lesend'
      ? ''
      : 'Gespeichertes bleibt, Vier-Augen-Anträge und Benachrichtigungen (mit „[Vorschau]“) gehen wirklich hinaus. ') +
    'Jobs laufen nicht von selbst, nur per Knopf in Verwaltung → Module.' +
    (v.ohneFreischaltung ? ' Das Modul ist in diesem Mandanten noch nicht freigeschaltet.' : '') +
    (seit ? ` Vorschau läuft seit ${seit}, bis zur Freigabe oder bis sie beendet wird.` : '')
  );
}

/** Beschriftung des Umschalters. */
export function umschalterText(v: VorschauEintrag, vorschauAn: boolean): string {
  return vorschauAn ? 'Freigegebene Version öffnen' : `Vorschau ${v.version} testen`;
}
